import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { BASE_SOCKET_URL, BASE_URL } from '../../redux/actions/ip';
import { io } from 'socket.io-client';
import Peer from 'simple-peer';
import { ICE_SERVERS } from '../../utils/iceConfig';

// Separate axios instance — intentionally bypasses the global 401 interceptor
// in App.js so that optional session-logging calls never trigger a logout.
const silentAxios = axios.create({ withCredentials: true });

const genRoomId = () => Math.random().toString(36).substr(2, 9);

const useGroupVideoCallFunctions = ({ socket }) => {
  const params = useParams();
  const history = useHistory();
  const userLogin = useSelector(state => state.userLogin);
  const currentUser = userLogin?.userInfo?.data?.user;

  // For direct/one-on-one calls, auto-start the meeting
  const searchParams = new URLSearchParams(window.location.search);
  const directCallUserId = searchParams.get('directCall');
  const shouldAutoStart = !!directCallUserId;

  const [callStarted, setCallStarted] = useState(shouldAutoStart);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState('chat');
  const [callSeconds, setCallSeconds] = useState(0);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [peers, setPeers] = useState([]); // array of { peer, name }
  const [localStream, setLocalStream] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [screenShareActive, setScreenShareActive] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [sharedFiles, setSharedFiles] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);

  const socketRef = useRef();
  const peersRef = useRef([]);
  const localStreamRef = useRef();
  const screenTrackRef = useRef();
  const leaveLoggedRef = useRef(false);

  // If there's no roomId, generate one and redirect
  const roomId = params.roomId || genRoomId();

  useEffect(() => {
    if (!params.roomId) {
      history.replace(`/group-video-call/${roomId}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logJoinSession = useCallback(async rid => {
    try {
      await silentAxios.post(`${BASE_URL}/video-sessions/${rid}/join`);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!callStarted) return;
    const interval = setInterval(() => setCallSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [callStarted]);

  // Warn before browser tab close / refresh — only when call is live
  useEffect(() => {
    if (!callStarted) return;
    const handleBeforeUnload = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [callStarted]);

  const logLeaveSession = useCallback(async rid => {
    if (leaveLoggedRef.current) return;
    leaveLoggedRef.current = true;
    try {
      await silentAxios.patch(`${BASE_URL}/video-sessions/${rid}/leave`);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!callStarted) return;
    const rid = params.roomId;
    if (!rid) return;

    socketRef.current = io.connect(BASE_SOCKET_URL, { withCredentials: true });

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMediaError('Your browser does not support video calls. Please use a modern browser with HTTPS.');
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then(stream => {
        setLocalStream(stream);
        localStreamRef.current = stream;

        socketRef.current.emit('join room', rid);
        logJoinSession(rid);

        socketRef.current.on('all users', users => {
          const newPeers = [];
          users.forEach(userID => {
            const peer = createPeer(userID, socketRef.current.id, stream);
            peersRef.current.push({ peerID: userID, peer, name: '' });
            newPeers.push({ peer, name: '' });
          });
          setPeers(newPeers);
        });

        // When new user joins room, create peer to them (we initiate)
        socketRef.current.on('new user joined', ({ newUserID }) => {
          const peer = createPeer(newUserID, socketRef.current.id, stream);
          peersRef.current.push({ peerID: newUserID, peer, name: '' });
          setPeers(prev => [...prev, { peer, name: '' }]);
        });

        socketRef.current.on('user joined', payload => {
          const peer = addPeer(payload.signal, payload.callerID, stream);
          const name = payload.callerName || '';
          peersRef.current.push({ peerID: payload.callerID, peer, name });
          setPeers(prev => [...prev, { peer, name }]);
        });

        socketRef.current.on('receiving returned signal', payload => {
          const item = peersRef.current.find(p => p.peerID === payload.id);
          if (item) item.peer.signal(payload.signal);
        });

        socketRef.current.on('user left', userID => {
          const peerObj = peersRef.current.find(p => p.peerID === userID);
          if (peerObj) peerObj.peer.destroy();
          peersRef.current = peersRef.current.filter(p => p.peerID !== userID);
          setPeers(peersRef.current.map(p => ({ peer: p.peer, name: p.name })));
        });

        socketRef.current.on('file-shared', payload => {
          setSharedFiles(prev => [...prev, payload]);
        });

        socketRef.current.on('in-call-chat', payload => {
          setChatMessages(prev => [...prev, payload]);
        });
      })
      .catch(err => {
        console.error('Error accessing media devices:', err);
        setMediaError(
          err.name === 'NotAllowedError'
            ? 'Camera and microphone access was denied. Please allow access in your browser settings.'
            : 'Could not access camera or microphone. Please check your device and try again.',
        );
      });

    return () => {
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
      peersRef.current.forEach(({ peer }) => {
        try {
          peer.destroy();
        } catch (_) {}
      });
      if (socketRef.current) socketRef.current.disconnect();
      logLeaveSession(rid);
    };
  }, [params.roomId, callStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPeer = (userToSignal, callerID, stream) => {
    const peer = new Peer({ initiator: true, trickle: false, stream, config: ICE_SERVERS });
    peer.on('signal', signal => {
      socketRef.current.emit('sending signal', { userToSignal, callerID, signal, callerName: currentUser?.name || '' });
    });
    return peer;
  };

  const addPeer = (incomingSignal, callerID, stream) => {
    const peer = new Peer({ initiator: false, trickle: false, stream, config: ICE_SERVERS });
    peer.on('signal', signal => {
      socketRef.current.emit('returning signal', { signal, callerID });
    });
    peer.signal(incomingSignal);
    return peer;
  };

  const handleToggleMic = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setMicOn(track.enabled);
      }
    }
  };

  const handleToggleVideo = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setVideoOn(track.enabled);
      }
    }
  };

  const handleScreenShare = async () => {
    if (screenShareActive) {
      if (screenTrackRef.current) screenTrackRef.current.stop();
      return;
    }
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({ cursor: true });
      const screenTrack = displayStream.getVideoTracks()[0];
      screenTrackRef.current = screenTrack;

      // Replace video track in all peer connections
      peersRef.current.forEach(({ peer }) => {
        const sender = peer._pc?.getSenders?.().find(s => s.track?.kind === 'video');
        if (sender) sender.replaceTrack(screenTrack);
      });

      setLocalStream(displayStream);
      setScreenShareActive(true);

      screenTrack.onended = () => {
        // Reacquire camera when user stops sharing
        navigator.mediaDevices
          .getUserMedia({ video: true, audio: true })
          .then(camStream => {
            const newCamTrack = camStream.getVideoTracks()[0];
            peersRef.current.forEach(({ peer }) => {
              const sender = peer._pc?.getSenders?.().find(s => s.track?.kind === 'video');
              if (sender) sender.replaceTrack(newCamTrack);
            });
            localStreamRef.current = camStream;
            setLocalStream(camStream);
          })
          .catch(() => {});
        setScreenShareActive(false);
      };
    } catch (err) {
      console.error('Screen share error:', err);
    }
  };

  const handleLeaveCall = async () => {
    const rid = params.roomId;
    if (screenTrackRef.current) screenTrackRef.current.stop();
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
    peersRef.current.forEach(({ peer }) => {
      try {
        peer.destroy();
      } catch (_) {}
    });
    if (socketRef.current) {
      socketRef.current.emit('user leaving', { roomId: rid });
      socketRef.current.disconnect();
    }
    await logLeaveSession(rid);
    history.push('/chat');
  };

  const handleSendChatMessage = text => {
    if (!text.trim() || !socketRef.current) return;
    const msg = {
      text: text.trim(),
      senderName: currentUser?.name || 'You',
      senderId: currentUser?._id || 'local',
      ts: Date.now(),
    };
    socketRef.current.emit('in-call-chat', msg);
    setChatMessages(prev => [...prev, { ...msg, isLocal: true }]);
  };

  const handleShareFile = ({ name, url }) => {
    const entry = { name, url, sharedBy: currentUser?.name || 'You', sharedAt: new Date().toISOString() };
    socketRef.current?.emit('share-file', entry);
    setSharedFiles(prev => [...prev, entry]);
  };

  const getRoomLink = () => {
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}#/guest-video/${roomId}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(getRoomLink()).catch(() => {});
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  const handlePanelTabClick = tab => {
    if (rightPanelOpen && rightPanelTab === tab) {
      setRightPanelOpen(false);
    } else {
      setRightPanelTab(tab);
      setRightPanelOpen(true);
    }
  };

  return {
    history,
    params,
    currentUser,
    callStarted,
    setCallStarted,
    rightPanelOpen,
    setRightPanelOpen,
    rightPanelTab,
    setRightPanelTab,
    callSeconds,
    showInviteModal,
    setShowInviteModal,
    peers,
    localStream,
    micOn,
    videoOn,
    screenShareActive,
    copySuccess,
    setCopySuccess,
    mediaError,
    sharedFiles,
    chatMessages,
    roomId,
    handleToggleMic,
    handleToggleVideo,
    handleScreenShare,
    handleLeaveCall,
    handleSendChatMessage,
    handleShareFile,
    getRoomLink,
    handleCopyLink,
    handlePanelTabClick,
  };
};

export default useGroupVideoCallFunctions;
